import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { ReactComponent as CloseSmallWhiteIcon } from '@assets/icons/close-small-white.svg';

import Editor from '@components/editor/Editor';
import LoaderComponent from '@components/LoaderComponent';
import { toastError, toastSuccess } from '@components/Toast';

import CommentReplyPanel from '@features/designTool/components/CommentReplyPanel';
import PostReplyList from '@features/designTool/components/PostReplyList';
import { SelectedFileViewComponent } from '@features/task/components/TaskManageComponents/SelectedFileViewComponent';

import { clearCurrentActivePage } from '@store/actions/ActivePageAction';
import { clearAllChats } from '@store/actions/ChatAction';
import { fetchDesignToolComment } from '@store/actions/DesignToolAction';

import ApiClient from '@lib/apiClient';

import {
	getMinsCountFromNow,
	getTimeCount,
	getUserInfo,
	isAdmin,
	onGetLocalFromUtc,
	parseAndRenderLinks
} from '@utils/Util';

import { SAMPLE_REQUEST_TYPES } from '@/constants/constant';

import CommonImage from '@/components/CommonImage';
import SampleRequestModal from '@/features/sampleDevelopment/components/SampleRequestModals/SampleRequestModal';
import DeleteConfirmationModal from '@/components/DeleteConfirmationModal';
import { useCancelTaskMutation } from '@/features/task-configuration/api/taskConfigurationApi';
import { useUpdateIssueMutation } from '@/features/design/api/designDetailsApi';

const DesignToolRightSideBar = ({ productId, selectedPostId, brandName = 'BRAND' }) => {
	const designToolCommentStore = useSelector((store) => store.designToolCommentStore);
	const [addComment, setAddComment] = useState(false);
	const [loader, setLoader] = useState(true);
	const [commentError, setCommentError] = useState('');
	const [selectedFiles, setSelectedFiles] = useState([]);
	const dispatch = useDispatch();
	const { id: paramsId } = useParams();
	const id = productId || paramsId;
	const postInputRef = useRef(null);
	const chatInfo = useRef(null);
	const searchRef = useRef();
	const dropdownRef = useRef(null);
	const [, setReadOnly] = useState(true);
	const [mentionSearch, setMentionSearch] = useState('');
	const [isBuyerMentioned, setIsBuyerMentioned] = useState(false);
	const [isNitexMentioned, setIsNitexMentioned] = useState(true);
	const [mentionToUsers, setMentionToUsers] = useState([]);
	const [selectedMentionedUser, setSelectedMentionedUser] = useState([]);
	const [allComentsData, setAllCommentsData] = useState(null);
	const [selectedPostTag, setSelectedPostTag] = useState('PRODUCT_DEVELOPMENT_COMMENT');
	const isNewPost = useSelector((store) => store.chatReducer.newMessage);
	const [editorContent, setEditorContent] = useState(null);
	const [mentionIds, setMentionIds] = useState([]);
	const [selectedComment, setSelectedComment] = useState(null);
	const getUser = getUserInfo();
	const [isShowConfirmationModal, setIsShowConfirmationModal] = useState(false);
	const [isShowEditPdRequest, setIsShowEditPdRequest] = useState(false);

	const [cancelTask, { isLoading: cancelingRequest }] = useCancelTaskMutation();
	const [updateIssue, { isLoading: issueUpdating }] = useUpdateIssueMutation();

	const scrollDownInChatBody = () => {
		let container = document.getElementById('taskPostScroll');
		let o1 = container.scrollHeight - container.clientHeight;
		let elem = document.getElementById('taskPostScroll');
		elem.scrollTop = o1;
	};

	useEffect(() => {
		setAllCommentsData(designToolCommentStore?.commentList);
		scrollDownInChatBody();
	}, [designToolCommentStore?.commentList]);

	useEffect(() => {
		(async () => {
			setLoader(true);
			await dispatch(fetchDesignToolComment(id, generateParams(0)));
			setLoader(false);
		})();
	}, [id, selectedPostTag]);

	const generateParams = (page = 0) => {
		let params = `?page=${page}&size=1000&sort=id,desc`;
		if (selectedPostTag) {
			params += `&postTag=${selectedPostTag}`;
		}
		return params;
	};

	useEffect(() => {
		chatInfo.current = designToolCommentStore?.commentList;
	}, [designToolCommentStore?.commentList]);

	useEffect(() => {
		scrollDownInChatBody();
		const postResponse = isNewPost;
		const cloneChatInfo = JSON.parse(JSON.stringify(allComentsData));

		if (postResponse?.text && getUserInfo()?.id !== postResponse?.postedBy.id) {
			if (postResponse?.parentPostId) {
				cloneChatInfo?.data.forEach((post) => {
					if (parseInt(post.id) === parseInt(postResponse?.parentPostId)) {
						post?.commentList.push(postResponse);
						toastSuccess('New message received');
						setTimeout(() => {
							dispatch(clearAllChats());
						}, 1000);
					}
				});
			} else {
				cloneChatInfo?.data.push(postResponse);
				cloneChatInfo.totalElements = cloneChatInfo?.totalElements + 1;
			}
			setAllCommentsData(cloneChatInfo);
			if (!postResponse?.parentPostId) {
				// setMinimize(false)
				toastSuccess('New message received');
				setTimeout(() => {
					dispatch(clearAllChats());
				}, 1000);
			}
		}
	}, [isNewPost?.id]);

	const handleScroll = (e) => {
		const bottom = e.target.scrollHeight - e.target.scrollTop === e.target.clientHeight;
		if (bottom && chatInfo?.current?.totalElements !== chatInfo?.current?.data?.length) {
			setLoader(true);
			dispatch(fetchDesignToolComment(id, generateParams(chatInfo?.current?.currentPage + 1)));
			setLoader(false);
		}
	};

	const renderTimeCount = (item) => {
		let prev = onGetLocalFromUtc(item?.dateAdded);
		const inMins = getMinsCountFromNow(prev, 'YYYY-MM-DD HH:mm:ss');
		const timeAgo = getTimeCount(inMins, prev, true);
		return <p className="regular-12 gray_dark_02 text-uppercase">{timeAgo}</p>;
	};

	const handleCancelSampleRequest = (item) => {
		cancelTask({
			body: {
				cancelLogId: item?.relatedInfo?.developmentJourneyLogId,
				postId: selectedComment?.id
			}
		})
			.unwrap()
			.then(() => {
				dispatch(fetchDesignToolComment(id, generateParams(0)));
				toastSuccess('Request canceled.');
				setIsShowConfirmationModal(false);
				setSelectedComment(null);
			})
			.catch((error) => {
				toastError(error?.data?.message || 'Something went wrong!');
			});
	};

	const handleMarkInvalidIssue = (item) => {
		const payload = {
			productId: item?.relatedInfo?.productId,
			status: 'INVALID',
			postId: item?.postedBy?.postId
		};

		updateIssue({ issueId: item?.relatedInfo?.productIssueId, body: { ...payload } })
			.unwrap()
			.then(() => {
				(async () => {
					setLoader(true);
					await dispatch(fetchDesignToolComment(id, generateParams(0)));
					setLoader(false);
				})();
			})
			.catch((error) => {
				toastError(error?.data?.message || 'Something went wrong!');
			});
	};

	const handleRequestDetails = (item) => {
		const REQUEST_DETAIL_COSTING_ID = item?.relatedInfo?.quoteRequestId;
		if (REQUEST_DETAIL_COSTING_ID) {
			window.open(`/costing/${REQUEST_DETAIL_COSTING_ID}`, '_blank');
		}
	};

	const renderComment = () => {
		return (
			allComentsData?.data?.length > 0 &&
			allComentsData?.data?.map((item, index) => {
				return (
					<div
						className={`single-comment-container ${
							item.id === selectedPostId ? 'highlight-wrapper' : ''
						}`}
						key={`design_tool_${index}`}
					>
						<div className="title d-flex">
							{item?.postPositionNo ? (
								<span className="comment-number">{item?.postPositionNo}</span>
							) : (
								<img
									src={
										item?.postedBy?.imageUrl
											? item?.postedBy?.imageUrl
											: `${process.env.PUBLIC_URL}/images/pro_pic_default.svg`
									}
									alt="profile"
									className="profile-image"
								/>
							)}

							<div className="ml-2">
								<span className="regular-14 gray_dark_01 weight-500">
									{item?.postedBy?.name}
								</span>
								{item?.artBoardName && (
									<span className="semibold-12 gray_dark_02 ml-1">
										{' '}
										on <span className="text-uppercase">{item?.artBoardName}</span>
									</span>
								)}
								{item?.isSeen === false && <span className="blue-dot ml-1"></span>}

								{renderTimeCount(item)}
							</div>
						</div>
						<div className="comments-description parent position-relative">
							<p
								dangerouslySetInnerHTML={{
									__html: parseAndRenderLinks(item?.text)
								}}
								className={`${item?.relatedInfo?.isTaskCancelled ? 'cursor-none' : ''} description-text ${item?.relatedInfo?.quoteRequestId && item?.text?.includes('Send costing request') ? 'pb-4' : ''}`}
								style={{
									wordWrap: 'break-word'
								}}
							/>
							{item?.relatedInfo?.quoteRequestId &&
								item?.text?.includes('Send costing request') && (
									<span
										className="request-detail-btn regular-12 blue text-underline cursor-pointer position-absolute"
										onClick={() => handleRequestDetails(item)}
									>
										Request details
									</span>
								)}
							<div className="comments-action d-flex align-items-center position-absolute">
								{item?.relatedInfo?.isTaskCancelled && (
									<div>
										<span className="status red-3">Cancel</span>
									</div>
								)}

								{item?.relatedInfo?.productIssueStatus === 'INVALID' && (
									<div className="mr-4">
										<span className="status red-3">Invalid issue</span>
									</div>
								)}
								{(isAdmin() ||
									parseInt(getUser?.id) === parseInt(item?.postedBy?.id) ||
									getUser?.primaryUserType === 'PRODUCT_DEVELOPMENT') &&
									item?.relatedInfo?.isCancellable &&
									!item?.relatedInfo?.isTaskCancelled && (
										<div className="option">
											<div className="dropdown">
												<button
													aria-expanded="false"
													className="btn btn-default dropdown-toggle p-0"
													data-toggle="dropdown"
													id="menu1"
													type="button"
												>
													<CommonImage
														src="/icons/more_icon.svg"
														width={24}
														height={24}
														alt="more_icon"
													/>
												</button>
												<ul
													aria-labelledby="menu1"
													className="dropdown-menu dropdown-menu-right shadow-lg"
													role="menu"
													x-placement="bottom-end"
												>
													{(isAdmin() ||
														parseInt(getUser?.id) ===
															parseInt(item?.postedBy?.id) ||
														getUser?.primaryUserType === 'PRODUCT_DEVELOPMENT') &&
														item?.taskValueType === 'SEND_TO_PD' && (
															<li
																className="px-4 pb-3 pt-3"
																role="presentation"
																onClick={() => {
																	setIsShowEditPdRequest(true);
																	setSelectedComment(item);
																}}
															>
																<span
																	className="font-weight-normal  text-black"
																	role="menuitem"
																	tabIndex={-1}
																>
																	Edit request
																</span>
															</li>
														)}
													{(isAdmin() ||
														parseInt(getUser?.id) ===
															parseInt(item?.postedBy?.id)) && (
														<li
															className="px-4 pb-3"
															role="presentation"
															onClick={() => {
																setSelectedComment(item);
																setIsShowConfirmationModal(true);
															}}
														>
															<span
																className="font-weight-normal  text-black"
																role="menuitem"
																tabIndex={-1}
															>
																Cancel request
															</span>
														</li>
													)}
												</ul>
											</div>
										</div>
									)}
								{/* // MARK ISSUE AS INVALID */}
								{isAdmin() && item?.relatedInfo?.productIssueId && (
									<div className="option">
										<div className="dropdown">
											<button
												aria-expanded="false"
												className="btn btn-default dropdown-toggle p-0"
												data-toggle="dropdown"
												id="validate"
												type="button"
											>
												<CommonImage
													src="/icons/more_icon.svg"
													width={24}
													height={24}
													alt="more_icon"
												/>
											</button>
											<ul
												aria-labelledby="validate"
												className="dropdown-menu dropdown-menu-right shadow-lg"
												role="menu"
												x-placement="bottom-end"
											>
												{isAdmin() && (
													<li
														className="px-4 pb-3 pt-3"
														// role="presentation"
														onClick={(e) => {
															e.stopPropagation();
															handleMarkInvalidIssue(item);
														}}
													>
														<span
															className="font-weight-normal  text-black"
															// role="menuitem"
															// tabIndex={-1}
														>
															Mark as invalid issue
														</span>
													</li>
												)}
											</ul>
										</div>
									</div>
								)}
							</div>
							{renderFilePreview(item?.docList, false)}
							<CommentReplyPanel
								parentPost={item}
								productId={id}
								toolType={'design-tool'}
								selectedPostTag={selectedPostTag}
								generateParams={generateParams}
							/>
						</div>
						<div className="reply-list">
							<PostReplyList
								commentList={item?.commentList}
								highlightReplyId={selectedPostId}
							/>
						</div>
					</div>
				);
			})
		);
	};

	const onMultipleFileSelect = (e, docType) => {
		const files = Array.from(e.target?.files);
		const fileList = [...selectedFiles];
		files.map((item) => {
			const data = {
				name: item?.name,
				docMimeType: item?.type,
				documentType: docType,
				print: false
			};
			const reader = new FileReader();
			reader.readAsDataURL(item);
			reader.onload = async () => {
				data.base64Str = reader?.result;
				fileList.push(data);
			};
		});

		setTimeout(() => {
			setSelectedFiles(fileList);
		}, 500);
	};

	const removeFile = (index) => {
		const fileList = selectedFiles.filter((file, i) => i !== index);
		setSelectedFiles(fileList);
	};

	const renderFilePreview = (fileList, removeOption) => {
		if (fileList?.length > 0) {
			return (
				<div className={'files-n-photos custom-scrollbar}'}>
					{fileList.map((file, i) => {
						return (
							<SelectedFileViewComponent
								showRemoveOption={removeOption}
								file={file}
								key={i}
								index={i}
								remove={removeFile}
							/>
						);
					})}
				</div>
			);
		}
	};

	const handleCancel = () => {
		setCommentError('');
		setAddComment(false);
		setSelectedFiles([]);
		setSelectedMentionedUser([]);
		postInputRef.current = null;
	};

	const handlePost = () => {
		let newComment = editorContent === null ? '' : editorContent.toString().replace('<p></p>', '').trim();
		if (!newComment) {
			setCommentError('Comment required');
			return;
		}
		setCommentError('');
		setLoader(true);
		let body = {
			documentDTOList: selectedFiles,
			productId: parseInt(id),
			postType: 'PRODUCT_DEVELOPMENT_COMMENT',
			postTag: selectedPostTag,
			text: editorContent,
			taggedUserIdList: mentionIds
		};
		body.recipientList = [];
		if (isNitexMentioned) body?.recipientList.push({ recipientType: 'NITEX' });

		if (isBuyerMentioned && brandName !== 'Nitex') body?.recipientList.push({ recipientType: 'BUYER' });

		if (selectedMentionedUser) {
			selectedMentionedUser.map((user) => {
				const userIdMap = {
					recipientType: 'INDIVIDUAL',
					userId: user?.memberId
				};
				body.recipientList.push(userIdMap);
			});
		}
		if (body?.recipientList?.length === 0) {
			toastError('At least one mention needed');
			setIsNitexMentioned(true);
			setIsBuyerMentioned(true);
			return;
		}
		ApiClient.POST('addNewPost', body)
			.then(() => {
				setSelectedFiles([]);
				dispatch(fetchDesignToolComment(id, generateParams(0)));
				setLoader(false);
				setAddComment(false);
				handleCancel();
			})
			.catch((error) => {
				setLoader(false);
				toastError(error?.response?.data?.message);
			});
	};

	useEffect(() => {
		document.body.addEventListener('keydown', handleKeyDown);
		return () => {
			document.body.removeEventListener('keydown', handleKeyDown);
			dispatch(clearCurrentActivePage());
		};
	}, []);

	const isQuillMentionListExist = () => {
		let quillLists = document.getElementById('quill-mention-list');
		return !!quillLists;
	};

	const handleKeyDown = (event) => {
		if (
			document.activeElement.getAttribute('class') === 'outline-none mention-search-box' ||
			document.activeElement.getAttribute('class') === 'single-item-focus'
		) {
			const isUp = event.key === 'ArrowUp';
			const isDown = event.key === 'ArrowDown';
			const inputIsFocused = document.activeElement === searchRef.current;
			const resultsItems = Array.from((dropdownRef.current && dropdownRef.current.children) || []);
			const activeResultIndex = resultsItems.findIndex((child) => {
				return child.querySelector('span') === document.activeElement;
			});

			if (isUp) {
				if (!isQuillMentionListExist()) {
					if (inputIsFocused) {
						resultsItems[resultsItems?.length - 1].querySelector('span').focus();
					} else if (resultsItems[activeResultIndex - 1]) {
						resultsItems[activeResultIndex - 1].querySelector('span').focus();
					} else {
						searchRef.current && searchRef.current.focus();
					}
				}
			}

			if (isDown) {
				if (!isQuillMentionListExist()) {
					if (inputIsFocused) {
						resultsItems[0].querySelector('span').focus();
					} else if (resultsItems[activeResultIndex + 1]) {
						resultsItems[activeResultIndex + 1].querySelector('span').focus();
					} else {
						searchRef.current && searchRef.current.focus();
					}
				}
			}
		}
	};

	const selectBuyer = () => {
		setIsBuyerMentioned(true);
		setMentionToUsers([]);
		setMentionSearch('');
	};

	const selectNitex = () => {
		setIsNitexMentioned(true);
		setMentionToUsers([]);
		setMentionSearch('');
	};

	const handleMentionTo = async (e) => {
		setMentionSearch(e.target.value);
		if (e.target.value) {
			const data = designToolCommentStore?.memberList?.filter(
				(member) =>
					member.email && member.email.toUpperCase().indexOf(e.target.value.toUpperCase()) !== -1
			);
			setMentionToUsers(data);
		} else {
			setMentionToUsers([]);
		}
	};

	const selectedUserToMention = (value) => {
		const selectedUser = mentionToUsers?.filter((el) => el.email === value);
		if (selectedUser?.length > 0) {
			let tmpArray = [...selectedMentionedUser];
			tmpArray.push(selectedUser[0]);
			setSelectedMentionedUser(tmpArray);
			setMentionSearch('');
			setMentionToUsers([]);
		}
	};

	const onDismissSelection = (userId) => {
		const userDismiss = [...selectedMentionedUser];
		const index = selectedMentionedUser.findIndex((el) => el.id === userId);
		userDismiss.splice(index, 1);
		setSelectedMentionedUser(userDismiss);
	};

	const renderUserMention = () => {
		if (addComment) {
			return (
				<div className="tag-persons d-flex flex-wrap" onClick={() => setReadOnly(true)}>
					<span className="mr-2">To</span>
					<div className="d-flex gap-2 flex-wrap">
						{isNitexMentioned && (
							<div className="flex items-center gap-2">
								<div className="tag-badge normal-case">
									<span>Nitex</span>
								</div>
							</div>
						)}
						{isBuyerMentioned && (
							<div className="d-flex flex-wrap items-center gap-2">
								<div className="tag-badge normal-case">
									<span>{brandName}</span>
									<span
										className="ml-1 cursor-pointer"
										onClick={() => setIsBuyerMentioned(false)}
									>
										<CloseSmallWhiteIcon />
									</span>
								</div>
							</div>
						)}
						{selectedMentionedUser &&
							selectedMentionedUser.map((user) => {
								return (
									<div key={user?.id} className="d-flex items-center gap-2">
										<div className="tag-badge normal-case ">
											<span>{user.email}</span>
											<span
												className="ml-1 cursor-pointer"
												onClick={() => onDismissSelection(user.id)}
											>
												<CloseSmallWhiteIcon />
											</span>
										</div>
									</div>
								);
							})}
					</div>
					<div className="position-relative ml-2" onClick={() => setReadOnly(true)}>
						<div className="suggetions bottom-full bg-white shadow-lg scroll-y-label">
							<ul
								className="max-h-[300px] custom-scroll overflow-y-auto pr-2"
								ref={dropdownRef}
							>
								{!isNitexMentioned && mentionSearch !== '' && (
									<li
										key={'nitex'}
										className="cursor-pointer px-3 py-1"
										onClick={selectNitex}
										onKeyPress={(e) => {
											if (e.key === 'Enter') {
												selectNitex();
											}
										}}
									>
										<span className="single-item-focus" tabIndex={0}>
											Nitex
										</span>
									</li>
								)}
								{!isBuyerMentioned && mentionSearch !== '' && (
									<li
										key={'buyer'}
										className="cursor-pointer px-3 py-1"
										onClick={selectBuyer}
										onKeyPress={(e) => {
											if (e.key === 'Enter') {
												selectBuyer();
											}
										}}
									>
										<span className="single-item-focus" tabIndex={0}>
											{brandName}
										</span>
									</li>
								)}
								{mentionToUsers?.map((user) => {
									return (
										<li
											key={user?.id}
											className="cursor-pointer px-3 py-1"
											onClick={() => selectedUserToMention(user?.email)}
											onKeyPress={(e) => {
												if (e.key === 'Enter') {
													selectedUserToMention(user?.email);
												}
											}}
										>
											<span className="single-item-focus" tabIndex={0}>
												{user?.email}
											</span>
										</li>
									);
								})}
							</ul>
						</div>
						<input
							type="text"
							autoComplete="off"
							className="outline-none mention-search-box"
							placeholder="Search with email"
							value={mentionSearch}
							onChange={handleMentionTo}
							onSelect={selectedUserToMention}
							ref={searchRef}
						/>
					</div>
				</div>
			);
		}
	};
	return (
		<LoaderComponent loading={loader || cancelingRequest || issueUpdating}>
			<div className="design-tool-sidebar comments-sidebar">
				<div className="design-tool-comments">
					<div className="comments-heading">
						<p className="regular-12 gray_dark_02 mb-0">COMMENTS</p>
					</div>
					<div className="conversation-filter-dropdown bg-white">
						<select
							className="w-100 mb-12"
							name="postTag"
							id="postTag"
							value={selectedPostTag}
							onChange={(e) => {
								setSelectedPostTag(e.target.value);
							}}
						>
							<option value={''}>All</option>
							<option value="PRODUCT_DEVELOPMENT_COMMENT">Design</option>
							<option value="PRODUCT_SAMPLE_COMMENT">Sample</option>
							<option value="PRODUCT_QUOTE_COMMENT">Costing</option>
						</select>
					</div>

					<div
						className={`all-comments-body custom-scrollbar ${addComment ? 'add-comments-footer-active' : ''}`}
						onScroll={handleScroll}
						id="taskPostScroll"
					>
						{allComentsData?.data?.length === 0 && !loader ? (
							<div className="no-comments">No comment found</div>
						) : (
							renderComment()
						)}
					</div>

					<div className="sidebar-comments-footer">
						<div className={addComment ? 'comments-container expand' : 'comments-container'}>
							{renderUserMention()}
							<div className="artboard-comment-box techpack-editor">
								{getUserInfo()?.profilePicDocument?.docUrl && (
									<img
										src={getUserInfo()?.profilePicDocument?.docUrl}
										alt="profile"
										className="profile"
									/>
								)}
								{!getUserInfo()?.profilePicDocument?.docUrl && (
									<img
										src={`${process.env.PUBLIC_URL}/images/pro_pic_default.svg`}
										alt="profile"
										className="profile"
									/>
								)}
								{!addComment ? (
									<textarea
										placeholder="Write comment..."
										onClick={() => {
											setAddComment(true);
										}}
									/>
								) : (
									<Editor
										setEditorContent={setEditorContent}
										setMentionIds={setMentionIds}
										memberList={designToolCommentStore?.memberList}
									/>
								)}
								{commentError && <p className="error">{commentError}</p>}
							</div>
							{renderFilePreview(selectedFiles, true)}
							{addComment && (
								<div className="artboard-comment-footer">
									<span className="upload">
										<label htmlFor="attachment">
											<img
												src={`${process.env.PUBLIC_URL}/icons/attach20.svg`}
												alt="attachment"
											/>
										</label>
										<input
											type="file"
											id="attachment"
											name="attachment"
											multiple
											onChange={(e) => onMultipleFileSelect(e, 'ACCESSORIES_DESIGN')}
										/>
									</span>
									<span className="action-buttons">
										<button onClick={handleCancel}>CANCEL</button>
										<button onClick={handlePost}>POST</button>
									</span>
								</div>
							)}
						</div>
					</div>
				</div>
			</div>
			{selectedComment?.taskValueType === 'SEND_TO_PD' && isShowEditPdRequest && (
				<SampleRequestModal
					productIds={[id]}
					onClose={() => {
						setSelectedComment(null);
						setIsShowEditPdRequest(false);
					}}
					requestType={SAMPLE_REQUEST_TYPES.SEND_TO_PD_IN_HOUSE}
					title="Send to PD"
					sampleId={[selectedComment?.relatedInfo?.sampleItemId]}
					isEdit={true}
					taskValueType={SAMPLE_REQUEST_TYPES.SEND_TO_PD_IN_HOUSE}
					isFromTechpack={true}
					selectedComment={selectedComment}
				/>
			)}
			{isShowConfirmationModal && (
				<DeleteConfirmationModal
					onClose={() => {
						setIsShowConfirmationModal(false);
						setSelectedComment(null);
					}}
					onConfirm={() => handleCancelSampleRequest(selectedComment)}
					title="Confirm Cancel Request"
					description="Are you sure you want to cancel this request?"
				/>
			)}
		</LoaderComponent>
	);
};

export default DesignToolRightSideBar;
